import { Check, Loader2, Pencil, X } from "lucide-react";
import {
  useGetIdentity,
  useListContext,
  useDataProvider,
  useNotify,
  useRecordContext,
  useTranslate,
  type Identifier,
} from "ra-core";
import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { CreateButton } from "@/components/admin/create-button";
import { DataTable } from "@/components/admin/data-table";
import { ExportButton } from "@/components/admin/export-button";
import { InfiniteList } from "@/components/admin/infinite-list";
import { SearchInput } from "@/components/admin/search-input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useScrollAreaRestoration } from "../misc/useScrollAreaRestoration";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { TopToolbar } from "../layout/TopToolbar";
import { FullHeightListLayout } from "../misc/FullHeightListLayout";
import { InfinitePagination } from "../misc/InfinitePagination";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { defaultTaskCategories } from "../root/defaultConfiguration";
import type { CrmDataProvider } from "../providers/supabase/dataProvider";
import type { Sale } from "../types";

const filters = [<SearchInput source="q" alwaysOn />];

// Inline edits staged per row — nothing is written until the save bar's Save.
type SaleEdit = Partial<
  Pick<Sale, "role" | "administrator" | "disabled" | "first_name" | "last_name">
>;

// The whole table is read-only until the toolbar's Edit toggle flips editMode;
// then every cell renders its input, prefilled. Staged edits commit via the
// save bar only (anti fast-finger), and leaving edit mode discards staging.
const SalesEditsContext = createContext<{
  editMode: boolean;
  setEditMode: (on: boolean) => void;
  edits: Record<Identifier, SaleEdit>;
  stage: (record: Sale, patch: SaleEdit) => void;
  clear: () => void;
}>({
  editMode: false,
  setEditMode: () => {},
  edits: {},
  stage: () => {},
  clear: () => {},
});

// Drops staged values that match the record again, so toggling back and forth
// leaves the row clean and the save bar count honest.
const pruneEdit = (record: Sale, edit: SaleEdit): SaleEdit => {
  const next: SaleEdit = { ...edit };
  if (next.role === (record.role ?? "sales")) delete next.role;
  if (next.administrator === record.administrator) delete next.administrator;
  if (next.disabled === (record.disabled ?? false)) delete next.disabled;
  if (next.first_name === record.first_name) delete next.first_name;
  if (next.last_name === record.last_name) delete next.last_name;
  return next;
};

const SalesEditsProvider = ({ children }: { children: ReactNode }) => {
  const [editMode, setEditModeState] = useState(false);
  const [edits, setEdits] = useState<Record<Identifier, SaleEdit>>({});

  const stage = useCallback((record: Sale, patch: SaleEdit) => {
    setEdits((prev) => {
      const pruned = pruneEdit(record, { ...prev[record.id], ...patch });
      const next = { ...prev };
      if (Object.keys(pruned).length) next[record.id] = pruned;
      else delete next[record.id];
      return next;
    });
  }, []);
  const clear = useCallback(() => setEdits({}), []);
  // Leaving edit mode always discards whatever wasn't saved.
  const setEditMode = useCallback((on: boolean) => {
    setEditModeState(on);
    if (!on) setEdits({});
  }, []);
  const value = useMemo(
    () => ({ editMode, setEditMode, edits, stage, clear }),
    [editMode, setEditMode, edits, stage, clear],
  );

  return (
    <SalesEditsContext.Provider value={value}>
      {children}
    </SalesEditsContext.Provider>
  );
};

const SalesListActions = () => {
  const { editMode, setEditMode } = useContext(SalesEditsContext);
  const translate = useTranslate();
  return (
    <TopToolbar>
      <Button
        variant={editMode ? "secondary" : "outline"}
        size="sm"
        className="cursor-pointer h-9"
        onClick={() => setEditMode(!editMode)}
      >
        {editMode ? <X /> : <Pencil />}
        {editMode
          ? translate("resources.sales.action.done_editing")
          : translate("ra.action.edit")}
      </Button>
      <ExportButton />
      <CreateButton label="resources.sales.action.new" />
    </TopToolbar>
  );
};

const NameCell = ({ field }: { field: "first_name" | "last_name" }) => {
  const record = useRecordContext<Sale>();
  const { editMode, edits, stage } = useContext(SalesEditsContext);
  if (!record) return null;
  const value = edits[record.id]?.[field] ?? record[field] ?? "";

  if (!editMode) return <span className="truncate">{value}</span>;
  return (
    <Input
      value={value}
      className="h-7 w-full max-w-44"
      onChange={(e) => stage(record, { [field]: e.target.value })}
    />
  );
};

const RoleCell = () => {
  const record = useRecordContext<Sale>();
  const { taskCategories } = useConfigurationContext();
  const { editMode, edits, stage } = useContext(SalesEditsContext);
  if (!record) return null;
  const categories = taskCategories ?? defaultTaskCategories;
  const value = edits[record.id]?.role ?? record.role ?? "sales";

  if (!editMode) {
    const label = categories.find((c) => c.value === value)?.label ?? value;
    return <Badge variant="secondary">{label}</Badge>;
  }
  return (
    <Select value={value} onValueChange={(role) => stage(record, { role })}>
      <SelectTrigger size="sm" className="h-7 w-full max-w-32 cursor-pointer">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {categories.map((c) => (
          <SelectItem key={c.value} value={c.value}>
            {c.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

// Admin / Disabled: a plain check mark in view mode, a checkbox in edit mode.
// Your own row is locked — you can't un-admin or disable yourself.
const BoolCell = ({ field }: { field: "administrator" | "disabled" }) => {
  const record = useRecordContext<Sale>();
  const { identity } = useGetIdentity();
  const { editMode, edits, stage } = useContext(SalesEditsContext);
  if (!record) return null;
  const isSelf = record.id === identity?.id;
  const value = edits[record.id]?.[field] ?? record[field] ?? false;

  if (!editMode) {
    return value ? <Check className="w-4 h-4 text-muted-foreground" /> : null;
  }
  return (
    <Checkbox
      checked={!!value}
      disabled={isSelf}
      className="cursor-pointer"
      onCheckedChange={(checked) =>
        stage(record, { [field]: checked === true })
      }
    />
  );
};

// Fixed footer: appears only when rows have staged edits. Save commits every
// dirty row through the users edge function and exits edit mode; Cancel
// discards and exits.
const SalesSaveBar = () => {
  const { edits, clear, setEditMode } = useContext(SalesEditsContext);
  const { data } = useListContext<Sale>();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const queryClient = useQueryClient();
  const notify = useNotify();
  const translate = useTranslate();
  const [saving, setSaving] = useState(false);

  const dirtyIds = Object.keys(edits);
  if (!dirtyIds.length) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      const rows = data ?? [];
      const results = await Promise.allSettled(
        Object.entries(edits).map(async ([id, patch]) => {
          const row = rows.find((r) => String(r.id) === String(id));
          if (!row) throw new Error(`Row ${id} not loaded`);
          // The users function resets omitted booleans, so always send the
          // full effective state, not just the staged fields.
          return dataProvider.salesUpdate(row.id, {
            email: row.email,
            first_name: patch.first_name ?? row.first_name,
            last_name: patch.last_name ?? row.last_name,
            administrator: patch.administrator ?? row.administrator,
            disabled: patch.disabled ?? row.disabled ?? false,
            role: patch.role ?? row.role ?? "sales",
          });
        }),
      );
      const failed = results.filter((r) => r.status === "rejected").length;
      queryClient.invalidateQueries({ queryKey: ["sales"] });
      if (failed) {
        notify(`${failed} of ${results.length} updates failed`, {
          type: "error",
        });
      } else {
        notify(translate("resources.sales.inline_save.success"), {
          type: "success",
        });
        clear();
        setEditMode(false);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      role="toolbar"
      className="flex items-center justify-between gap-4 px-4 py-3 border-t bg-background shrink-0"
    >
      <span className="text-sm text-muted-foreground">
        {translate("resources.sales.inline_save.pending", {
          smart_count: dirtyIds.length,
        })}
      </span>
      <div className="flex gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setEditMode(false)}
          disabled={saving}
          className="cursor-pointer"
        >
          {translate("ra.action.cancel")}
        </Button>
        <Button
          size="sm"
          onClick={handleSave}
          disabled={saving}
          className="cursor-pointer"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          {translate("ra.action.save")}
        </Button>
      </div>
    </div>
  );
};

export function SalesList() {
  return (
    <SalesEditsProvider>
      <InfiniteList
        filters={filters}
        actions={<SalesListActions />}
        sort={{ field: "first_name", order: "ASC" }}
        perPage={50}
      >
        <SalesListLayout />
      </InfiniteList>
    </SalesEditsProvider>
  );
}

// Percentage column widths so switching view/edit mode (badges ⇄ inputs) and
// value changes never reflow the table.
const SalesListLayout = () => {
  const viewportRef = useScrollAreaRestoration();
  return (
    <FullHeightListLayout footer={<SalesSaveBar />}>
      <ScrollArea viewportRef={viewportRef} className="flex-1 min-h-0">
        <DataTable rowClick={false}>
          <DataTable.Col source="first_name" className="w-[16%]">
            <NameCell field="first_name" />
          </DataTable.Col>
          <DataTable.Col source="last_name" className="w-[16%]">
            <NameCell field="last_name" />
          </DataTable.Col>
          <DataTable.Col source="email" className="w-[30%]" />
          <DataTable.Col
            source="role"
            label="resources.sales.fields.role"
            className="w-[14%]"
          >
            <RoleCell />
          </DataTable.Col>
          <DataTable.Col
            source="administrator"
            label="resources.sales.fields.administrator"
            className="w-[12%]"
          >
            <BoolCell field="administrator" />
          </DataTable.Col>
          <DataTable.Col
            source="disabled"
            label="resources.sales.fields.disabled"
            className="w-[12%]"
          >
            <BoolCell field="disabled" />
          </DataTable.Col>
        </DataTable>
        <InfinitePagination />
      </ScrollArea>
    </FullHeightListLayout>
  );
};
